import * as React from "react";
import { useContext, useEffect } from "react";
import { InputChangeEvent } from "@progress/kendo-react-inputs/dist/npm/input/interfaces/InputChangeEvent";
import { NumericTextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { ComboBoxChangeEvent } from "@progress/kendo-react-dropdowns";
import { FormGroup } from "@mui/material";
import Row from "../../../../Layout/PageGrid/Row";
import Col from "../../../../Layout/PageGrid/Col";
import LtlTabTable from "./LtlTabTable/LtlTabTable";
import TextInput from "../../../../Layout/Inputs/TextInput";
import GroupHeadline from "../../../../Layout/PageHeadline/GroupHeadline";
import { SpdrClientViewModel } from "../../../ViewModel/SpdrClientViewModel";
import { EditProjectContext } from "../../EditProjectContext";
import { SupplierTransitTime } from "./SupplierTransitTime";
import { BidpackInput } from "./BidpackInput";
import { CrossingCountries } from "./CrossingCountries";
import { LtlPipelineViewModel } from "../../../ViewModel/LtlPipelineViewModel";
import { setPropertyOnLtlPipeline } from "./SetPropertyOnLtlPipeline";
import { setLtlBidpackAndShipFrequency } from "./SetLtlBidpackAndShipFrequency";
import { SfMultiColumnDropDown } from "../FtlTab/InboundPipeline/SfMultiColumnDropDown";
import { PdsrClientViewModel } from "../../../ViewModel/PdsrClientViewModel";
import { getIdOfSpdrOrPdsr } from "./GetIdOfSpdrOrPdsr";
import SpdrOrPdsrText from "./SpdrOrPdsrText";
import { MdmSwitch } from "../../../../Layout/PageMenu/MdmSwitch";
import { isProjectReadOnly as isProjectReadonly } from "../../IsProjectReadOnly";
import { ReadOnlyProps } from "../../ReadOnlyProps";

type Props = {
  readonly spdrOrPdsr: SpdrClientViewModel | PdsrClientViewModel;
  readonly toggleJitKanban: (spdrId: number) => void;
  readonly toggleOutOfScopeSwitch: (checked: boolean, ltlPipeline: LtlPipelineViewModel) => void;
  readonly handleLaunchBatchChange: (spdrPdsrId: number, launchBatch: number | undefined) => void;
  ltlPipeline: LtlPipelineViewModel | undefined;
  ltlPipelineRoutingEmptyAndSpdrOrPdsrOutOfScope: boolean;
};

export const BaseLtlTabView = ({
  spdrOrPdsr,
  toggleJitKanban,
  ltlPipeline,
  toggleOutOfScopeSwitch,
  handleLaunchBatchChange,
  ltlPipelineRoutingEmptyAndSpdrOrPdsrOutOfScope,
  isReadOnly,
}: Props & ReadOnlyProps) => {
  const editProjectContext = useContext(EditProjectContext);
  const { project } = editProjectContext;
  const spdrOrPdsrId = getIdOfSpdrOrPdsr(spdrOrPdsr);

  if (!project) {
    throw new Error("project is undefined");
  }

  if (!ltlPipeline) {
    throw new Error("ltlPipeline undefined");
  }

  useEffect(() => {
    if (!ltlPipeline.LtlBidpack && !ltlPipeline.isBidpackLoaded) {
      setLtlBidpackAndShipFrequency(spdrOrPdsrId, ltlPipeline, editProjectContext);
    }
  }, [spdrOrPdsrId, ltlPipeline]);

  function onJitKanbanChange() {
    if (!spdrOrPdsrId) {
      return;
    }
    toggleJitKanban(spdrOrPdsrId);
  }

  const handleChanges = (event: InputChangeEvent | NumericTextBoxChangeEvent) => {
    const { name, value } = event.target;

    if (!name || !value) {
      return;
    }

    setPropertyOnLtlPipeline(spdrOrPdsrId, name, value, editProjectContext);
  };

  const handleSfChanges = (event: ComboBoxChangeEvent) => {
    if (!event.target.value) {
      return;
    }
    const { value } = event.target.value;
    if (!value) {
      return;
    }

    setPropertyOnLtlPipeline(spdrOrPdsrId, "SupplierShipFrequency", value, editProjectContext);
  };

  const projectReadonly = isProjectReadonly(project);
  return (
    <>
      <GroupHeadline>
        <SpdrOrPdsrText spdrOrPdsr={spdrOrPdsr} />
      </GroupHeadline>
      <Row gap="25px" marginBottom="2rem">
        <Col width="30%">
          <BidpackInput ltlPipeline={ltlPipeline} />
        </Col>
        <Col width="15%">
          <SupplierTransitTime ltlPipeline={ltlPipeline} />
        </Col>
        <Col width="15%">
          <SfMultiColumnDropDown
            onChange={handleSfChanges}
            value={ltlPipeline.SupplierShipFrequency ?? ""}
            label="Engineered SF"
            disabled={isReadOnly || ltlPipelineRoutingEmptyAndSpdrOrPdsrOutOfScope}
          />
        </Col>

        <Col width="15%">
          <TextInput
            label="Launch Batch"
            value={ltlPipeline.LaunchBatch ?? ""}
            onChange={(event) =>
              handleLaunchBatchChange(
                spdrOrPdsrId,
                event.target.value ? Number(event.target.value) : undefined
              )
            }
            name="LaunchBatch"
            readOnly={isReadOnly}
            disabled={isReadOnly}
          />
        </Col>

        <Col width="25%" align="right">
          <FormGroup sx={{ alignContent: "end" }}>
            <MdmSwitch
              label="Unrouted"
              isChecked={ltlPipelineRoutingEmptyAndSpdrOrPdsrOutOfScope}
              handleOnChange={(checked: boolean) => toggleOutOfScopeSwitch(checked, ltlPipeline)}
              disabled={isReadOnly || projectReadonly}
            />
            <MdmSwitch
              label="JIT / KANBAN"
              isChecked={ltlPipeline.JitKanban}
              defaultValue="false"
              handleOnChange={() => onJitKanbanChange()}
              disabled={
                isReadOnly || projectReadonly || ltlPipelineRoutingEmptyAndSpdrOrPdsrOutOfScope
              }
            />
          </FormGroup>
        </Col>
      </Row>
      {!ltlPipeline.JitKanban ? (
        <LtlTabTable
          spdrOrPdsrId={spdrOrPdsrId}
          ltlPipeline={ltlPipeline}
          unrouted={ltlPipelineRoutingEmptyAndSpdrOrPdsrOutOfScope}
          isReadOnly={projectReadonly || isReadOnly}
        />
      ) : (
        <div />
      )}
      <Row gap="25px" marginBottom="1rem">
        <Col width="30%">
          <TextInput
            label="Transit Time Adjustment in Days"
            name="TTAdjustment"
            value={ltlPipeline.TTAdjustment}
            disabled={projectReadonly || isReadOnly}
            onChange={handleChanges}
            type="number"
            readOnly={ltlPipelineRoutingEmptyAndSpdrOrPdsrOutOfScope || isReadOnly}
          />
        </Col>
        <Col width="70%">
          <CrossingCountries ltlPipeline={ltlPipeline} />
        </Col>
      </Row>
    </>
  );
};
